import { LitElement, html, css } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { StoreMixin } from '../store.js'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'
import { formatDate } from '../status.js'
import './rich-editor.js'

// KB article reader (§11). Body is agent-authored markdown rendered + sanitized on
// the server (bodyHtml), so it's injected as trusted HTML. Agents get edit/delete.
class PageKbArticle extends StoreMixin(LitElement) {
  static properties = {
    params: { type: Array },
    _article: { state: true },
    _loading: { state: true },
    _notFound: { state: true },
    _editing: { state: true },
    _title: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; }
    .top { display: flex; align-items: baseline; gap: 0.75rem; margin-bottom: 0.5rem; flex-wrap: wrap; }
    .back { cursor: pointer; color: var(--bx-brand); text-decoration: none; font-size: 0.9rem; }
    .crumb { color: var(--bx-muted); font-size: 0.85rem; }
    h1 { color: var(--bx-ink); margin: 0.25rem 0; font-size: 1.5rem; }
    .byline { color: var(--bx-muted); font-size: 0.82rem; margin-bottom: 1rem; }
    .actions { display: flex; gap: 0.5rem; margin-left: auto; }
    button { font: inherit; cursor: pointer; padding: 0.4rem 0.8rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.85rem; }
    button.primary { background: var(--bx-accent); color: var(--bx-accent-ink); border-color: transparent; font-weight: 700; }
    button.danger { color: #b42318; border-color: #f3c4c4; }
    .article { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
               box-shadow: var(--bx-shadow); padding: 1.5rem; color: var(--bx-ink); line-height: 1.6; }
    .article :first-child { margin-top: 0; }
    .article h1, .article h2, .article h3 { color: var(--bx-ink); }
    .article a { color: var(--bx-brand); }
    .article img { max-width: 100%; height: auto; border-radius: var(--bx-radius); }
    .article pre { background: var(--bx-bg); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
                   padding: 0.8rem; overflow-x: auto; }
    .article code { font-family: ui-monospace, monospace; font-size: 0.9em; }
    .article iframe, .article video { max-width: 100%; width: 100%; aspect-ratio: 16 / 9; height: auto;
                   border: 0; border-radius: var(--bx-radius); background: #000; }
    .article table { border-collapse: collapse; }
    .article th, .article td { border: 1px solid var(--bx-border); padding: 0.4rem 0.6rem; }
    .edit input, .edit textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 0.55rem;
             border: 1px solid var(--bx-border); border-radius: var(--bx-radius); margin-bottom: 0.6rem;
             background: var(--bx-bg); color: var(--bx-ink); }
    .edit textarea { min-height: 320px; resize: vertical; font-family: ui-monospace, monospace; font-size: 0.9rem; }
    .hint { color: var(--bx-muted); font-size: 0.8rem; margin: 0 0 0.6rem; }
    .loading, .notfound { padding: 3rem; text-align: center; color: var(--bx-muted); }
  `

  constructor() {
    super()
    this._loading = true
    this._notFound = false
    this._editing = false
    this._busy = false
  }

  connectedCallback() { super.connectedCallback(); this._load() }
  get _id() { return this.params?.[0] }
  get _isAgent() { return this.appState.user?.role === 'agent' }

  async _load() {
    this._loading = true; this._notFound = false
    try {
      this._article = await api.get(`/kb/articles/${this._id}`)
    } catch (err) {
      if (err.statusCode === 404) this._notFound = true
      else toast(err.message || 'Failed to load article', 'error')
    } finally { this._loading = false }
  }

  _startEdit() {
    this._title = this._article.title
    this._editing = true   // the editor is prefilled from the article via initialHtml
  }

  async _save() {
    const editor = this.renderRoot.querySelector('rich-editor')
    if (!this._title.trim() || !editor || editor.isEmpty()) { toast('Title and body are required', 'info'); return }
    this._busy = true
    try {
      const { html: body } = editor.getContent()
      this._article = await api.patch(`/kb/articles/${this._id}`, { title: this._title.trim(), body })
      this._editing = false
      toast('Saved', 'success')
    } catch (err) { toast(err.message || 'Save failed', 'error') } finally { this._busy = false }
  }

  async _delete() {
    if (!confirm(`Delete "${this._article.title}"? This can't be undone.`)) return
    try {
      await api.delete(`/kb/articles/${this._id}`)
      toast('Article deleted', 'success')
      navigate('/kb')
    } catch (err) { toast(err.message || 'Delete failed', 'error') }
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    if (this._notFound) return html`<div class="notfound">Article not found, or you don't have access.</div>`
    const a = this._article
    if (this._editing) {
      return html`
        <div class="top"><a class="back" @click=${() => (this._editing = false)}>← Cancel</a></div>
        <div class="article edit">
          <input .value=${this._title} @input=${e => (this._title = e.target.value)} placeholder="Title" />
          <p class="hint">Use the toolbar to format; the video button embeds a YouTube or Vimeo link.</p>
          <rich-editor allowVideo .initialHtml=${a.body} .placeholder=${'Write the article…'}></rich-editor>
          <button class="primary" style="margin-top:0.6rem;" ?disabled=${this._busy} @click=${this._save}>${this._busy ? 'Saving…' : 'Save'}</button>
        </div>`
    }
    return html`
      <div class="top">
        <a class="back" @click=${() => navigate('/kb')}>← Knowledge Base</a>
        <span class="crumb">${a.folderName}</span>
        ${this._isAgent ? html`<span class="actions">
          <button @click=${this._startEdit}>Edit</button>
          <button class="danger" @click=${this._delete}>Delete</button>
        </span>` : ''}
      </div>
      <h1>${a.title}</h1>
      <div class="byline">${a.authorName ? `By ${a.authorName} · ` : ''}Updated ${formatDate(a.updatedAt)}</div>
      <div class="article">${unsafeHTML(a.bodyHtml)}</div>
    `
  }
}

customElements.define('page-kb-article', PageKbArticle)
